<script lang="ts">
	/**
	 * Coupons (Design §4.1).
	 *
	 * A coupon's discount *terms* — percentage, per-currency amounts, duration — are not editable:
	 * they are copied onto every subscription that redeemed the code, so editing them would leave
	 * two customers holding "SAVE20" with different discounts and nothing recording which was
	 * which. Retire the code and issue a new one. What remains editable is the scope: the
	 * redemption limit, the expiry, the products it applies to, and whether it is live at all.
	 */

	import {
		Badge,
		Button,
		Checkbox,
		Input,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell,
		Toggle
	} from 'flowbite-svelte';
	import { createEventDispatcher } from 'svelte';
	import { ConfirmButton, EmptyState, FormField, FormModal, Panel, SelectField } from '../../../components';
	import { createCoupon, updateCoupon } from '../../../AppNetwork';
	import { toastError, toastSuccess } from '../../../stores/toast';
	import { formatFriendlyDate, formatMoney, majorUnitsFrom, minorUnitsFrom } from '../../../Utilities';
	import type { Coupon, Product, ProductFamily, StorefrontCurrency } from '../../../AppTypes';

	export let coupons: Coupon[] = [];
	/** For the "applies to" restriction. Empty simply offers no products to restrict to. */
	export let products: Product[] = [];
	/**
	 * Lines to restrict to. A family scope covers every plan in the line **including ones added
	 * later**, which is what listing today's plans one by one cannot express.
	 */
	export let families: ProductFamily[] = [];
	export let currencies: StorefrontCurrency[] = [];
	export let loading: boolean = false;

	const dispatcher = createEventDispatcher();

	let isAdding: boolean = false;
	let saving: boolean = false;
	let busyCode: string = '';
	let draft = {
		code: '',
		discountType: 'percent',
		percentOff: 10,
		duration: 'once',
		durationInMonths: 3,
		maxRedemptions: '',
		expiresAt: '',
		appliesToProductUUIDs: [] as string[],
		appliesToFamilySlugs: [] as string[],
		amounts: {} as Record<string, string>
	};

	/**
	 * The coupon being edited, addressed by its code.
	 *
	 * **The discount terms are absent here on purpose.** A coupon's percentage and per-currency
	 * amounts are copied onto every subscription that redeemed it, so editing them after the fact
	 * would leave two customers holding "SAVE20" with different discounts and nothing recording
	 * which was which. What *is* editable is how far the coupon still reaches: how many more
	 * redemptions it allows, when it stops, and which products it applies to.
	 */
	let editingCode: string = '';
	let edit = {
		maxRedemptions: '',
		expiresAt: '',
		appliesToProductUUIDs: [] as string[],
		appliesToFamilySlugs: [] as string[]
	};

	$: editing = coupons.find(coupon => coupon.code === editingCode) ?? null;

	/** Toggling one entry of a restriction list, kept out of the markup so it stays readable. */
	function fnToggle(list: string[], value: string, event: Event): string[] {
		return (event.target as HTMLInputElement).checked
			? [...list, value]
			: list.filter(existing => existing !== value);
	}

	/**
	 * What the two lists add up to, in words, for whichever form is open.
	 *
	 * Both empty is **every product**, which is the opposite of what an empty checkbox list looks
	 * like — so it is spelled out rather than left to be inferred from nothing being ticked.
	 */
	function fnScopeSummary(productUUIDs: string[], familySlugs: string[]): string {
		if (productUUIDs.length + familySlugs.length === 0) {
			return 'Applies to every product.';
		}
		const parts: string[] = [];
		if (familySlugs.length > 0) {
			parts.push(`${familySlugs.length} line${familySlugs.length === 1 ? '' : 's'}`);
		}
		if (productUUIDs.length > 0) {
			parts.push(`${productUUIDs.length} product${productUUIDs.length === 1 ? '' : 's'}`);
		}
		return `Applies to ${parts.join(' and ')}.`;
	}

	function fnStartEdit(coupon: Coupon): void {
		editingCode = coupon.code;
		edit = {
			maxRedemptions: coupon.maxRedemptions === null ? '' : String(coupon.maxRedemptions),
			// `datetime-local` wants `YYYY-MM-DDTHH:mm` and rejects the seconds and zone an ISO
			// string carries, so the stored value is trimmed rather than handed over whole.
			expiresAt: coupon.expiresAt === null ? '' : coupon.expiresAt.slice(0, 16),
			appliesToProductUUIDs: [...(coupon.appliesToProductUUIDs ?? [])],
			appliesToFamilySlugs: [...(coupon.appliesToFamilySlugs ?? [])]
		};
	}

	async function fnSaveEdit(): Promise<void> {
		if (editing === null) {
			return;
		}
		const code = editing.code;
		saving = true;
		try {
			await updateCoupon(code, {
				// Empty means unlimited, which is a value rather than an absence.
				maxRedemptions:
					`${edit.maxRedemptions}`.trim().length === 0 ? null : Number(edit.maxRedemptions),
				expiresAt: edit.expiresAt.length === 0 ? null : new Date(edit.expiresAt).toISOString(),
				// Null, not `[]`: an empty list is "restricted to nothing", which no coupon
				// should ever be, while null is the unrestricted coupon the empty form means.
				appliesToProductUUIDs:
					edit.appliesToProductUUIDs.length === 0 ? null : edit.appliesToProductUUIDs,
				appliesToFamilySlugs:
					edit.appliesToFamilySlugs.length === 0 ? null : edit.appliesToFamilySlugs
			});
			toastSuccess(`${code} saved.`);
			editingCode = '';
			dispatcher('changed');
		} catch (error) {
			toastError('The coupon was not saved.', (error as Error).message);
		} finally {
			saving = false;
		}
	}

	$: codeError =
		draft.code.length === 0 || /^[A-Za-z0-9_-]{3,60}$/.test(draft.code)
			? ''
			: 'Three or more letters, digits, hyphens and underscores.';
	$: canCreate = !saving && codeError === '' && draft.code.length >= 3;

	/**
	 * The decimal places a currency uses, for the amount fields.
	 *
	 * Two is **not** the fallback. JPY has none, and a form that assumes two reads ¥1,200 as ¥12 —
	 * silently, because both figures look like money.
	 */
	function fnDigitsOf(code: string): number {
		return currencies.find(currency => currency.code === code)?.minorUnitDigits ?? 0;
	}

	/** What the admin typed, as minor units, or null when it is not a money amount. */
	function fnMinorFrom(code: string, value: string): number | null {
		return minorUnitsFrom(value, fnDigitsOf(code));
	}

	function fnFormat(code: string, amountMinor: number): string {
		const row = currencies.find(currency => currency.code === code);
		return row === undefined ? `${amountMinor} (${code.toUpperCase()})` : formatMoney(amountMinor, row);
	}

	function fnSummary(coupon: Coupon): string {
		if (coupon.discountType === 'percent') return `${coupon.percentOff}% off`;
		return coupon.amounts.map(amount => fnFormat(amount.currency, amount.amountMinor)).join(', ') || 'No amount';
	}

	async function fnCreate(): Promise<void> {
		/*
		 * A blank currency is left out — that is how a coupon is made inapplicable in it. An
		 * *unparseable* one is left out too rather than sent as zero: a typo read as zero is a
		 * coupon that discounts nothing while claiming to, which no test of the arithmetic catches.
		 */
		const amounts = Object.entries(draft.amounts)
			.map(([currency, value]) => ({ currency, amountMinor: fnMinorFrom(currency, `${value}`) }))
			.filter((entry): entry is { currency: string; amountMinor: number } => entry.amountMinor !== null);

		saving = true;
		try {
			await createCoupon({
				code: draft.code.trim(),
				discountType: draft.discountType as 'percent' | 'fixed',
				percentOff: draft.discountType === 'percent' ? Number(draft.percentOff) : undefined,
				duration: draft.duration as 'once' | 'repeating' | 'forever',
				durationInMonths: draft.duration === 'repeating' ? Number(draft.durationInMonths) : undefined,
				maxRedemptions: `${draft.maxRedemptions}`.trim().length === 0 ? null : Number(draft.maxRedemptions),
				expiresAt: draft.expiresAt.length === 0 ? null : new Date(draft.expiresAt).toISOString(),
				// Null, not `[]` — an empty list restricts the coupon to nothing.
				appliesToProductUUIDs:
					draft.appliesToProductUUIDs.length === 0 ? null : draft.appliesToProductUUIDs,
				appliesToFamilySlugs:
					draft.appliesToFamilySlugs.length === 0 ? null : draft.appliesToFamilySlugs,
				amounts: draft.discountType === 'fixed' ? amounts : undefined
			});
			toastSuccess(`${draft.code.trim()} created.`);
			draft = {
				code: '',
				discountType: 'percent',
				percentOff: 10,
				duration: 'once',
				durationInMonths: 3,
				maxRedemptions: '',
				expiresAt: '',
				appliesToProductUUIDs: [],
				appliesToFamilySlugs: [],
				amounts: {}
			};
			isAdding = false;
			dispatcher('changed');
		} catch (error) {
			toastError('The coupon was not created.', (error as Error).message);
		} finally {
			saving = false;
		}
	}

	async function fnRetire(coupon: Coupon, isActive: boolean): Promise<void> {
		busyCode = coupon.code;
		try {
			await updateCoupon(coupon.code, { isActive });
			toastSuccess(`${coupon.code} ${isActive ? 'reactivated' : 'retired'}.`);
			dispatcher('changed');
		} catch (error) {
			toastError('The coupon was not changed.', (error as Error).message);
			dispatcher('changed');
		} finally {
			busyCode = '';
		}
	}
</script>

<Panel title="Coupons" faIcon="ticket">
	<svelte:fragment slot="titleRight">
		<Button size="sm" color="primary" on:click={() => (isAdding = !isAdding)}>
			<i class="fa-solid fa-plus mr-2" />Add coupon
		</Button>
	</svelte:fragment>

	{#if loading}
		<EmptyState variant="loading" title="Loading coupons…" />
	{:else if coupons.length === 0}
		<EmptyState title="No coupon yet." description="Discount codes are optional.">
			<Button color="primary" size="sm" on:click={() => (isAdding = true)}>Add the first coupon</Button>
		</EmptyState>
	{:else}
		<Table divClass="relative overflow-x-auto">
			<TableHead>
				<TableHeadCell>Code</TableHeadCell>
				<TableHeadCell>Discount</TableHeadCell>
				<TableHeadCell>Duration</TableHeadCell>
				<TableHeadCell>Redeemed</TableHeadCell>
				<TableHeadCell>Expires</TableHeadCell>
				<TableHeadCell>Live</TableHeadCell>
			</TableHead>
			<TableBody>
				{#each coupons as coupon (coupon.code)}
					<TableBodyRow>
						<TableBodyCell class="dataTable font-mono uppercase">{coupon.code}</TableBodyCell>
						<TableBodyCell class="dataTable">{fnSummary(coupon)}</TableBodyCell>
						<TableBodyCell class="dataTable">
							{coupon.duration === 'repeating' ? `${coupon.durationInMonths} months` : coupon.duration}
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							{coupon.redemptionCount}{coupon.maxRedemptions === null ? '' : ` / ${coupon.maxRedemptions}`}
							{#if coupon.maxRedemptions !== null && coupon.redemptionCount >= coupon.maxRedemptions}
								<Badge color="dark" class="ml-2">Exhausted</Badge>
							{/if}
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							{coupon.expiresAt === null ? '—' : formatFriendlyDate(coupon.expiresAt)}
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							{#if coupon.isActive}
								<ConfirmButton
									size="xs"
									color="alternative"
									disabled={busyCode === coupon.code}
									confirmText={`Retire ${coupon.code}? Nobody new can redeem it. Customers already holding it keep the discount they were sold.`}
									on:confirm={() => fnRetire(coupon, false)}
								>
									Retire
								</ConfirmButton>
							{:else}
								<Button size="xs" color="alternative" disabled={busyCode === coupon.code} on:click={() => fnRetire(coupon, true)}>
									Reactivate
								</Button>
							{/if}
							<Button size="xs" color="alternative" class="ml-2" on:click={() => fnStartEdit(coupon)}>
								<i class="fa-solid fa-pen mr-1" />Edit
							</Button>
						</TableBodyCell>
					</TableBodyRow>
				{/each}
			</TableBody>
		</Table>
	{/if}

	{#if editing !== null}
		<FormModal
			open={true}
			title={`Edit ${editing.code}`}
			faIcon="pen"
			submitLabel="Save coupon"
			size="md"
			busy={saving}
			description="The discount itself cannot change: it is copied onto every subscription that redeemed it, and two customers holding one code must not hold two different discounts. Retire the code and issue a new one instead."
			on:submit={fnSaveEdit}
			on:cancel={() => (editingCode = '')}
		>
			<div class="grid md:grid-cols-2 gap-4">
				<FormField
					id="coupon-edit-max"
					label="Maximum redemptions"
					description={`Empty is unlimited. Already redeemed ${editing.redemptionCount} time${editing.redemptionCount === 1 ? '' : 's'}.`}
				>
					<Input
						id="coupon-edit-max"
						type="number"
						min="1"
						step="1"
						bind:value={edit.maxRedemptions}
						disabled={saving}
					/>
				</FormField>

				<FormField id="coupon-edit-expires" label="Expires" description="Empty never expires.">
					<Input id="coupon-edit-expires" type="datetime-local" bind:value={edit.expiresAt} disabled={saving} />
				</FormField>
			</div>

			<div class="grid md:grid-cols-2 gap-4">
				<FormField
					label="Product families it applies to"
					description="A product family covers every product in it, including products added later."
				>
					<div class="space-y-2">
						{#each families as family (family.slug)}
							<label class="flex items-center gap-2 text-sm">
								<Checkbox
									checked={edit.appliesToFamilySlugs.includes(family.slug)}
									disabled={saving}
									spacing=""
									class="shrink-0"
									on:change={event =>
										(edit.appliesToFamilySlugs = fnToggle(edit.appliesToFamilySlugs, family.slug, event))}
								/>
								{family.name}
							</label>
						{:else}
							<p class="text-xs text-default">No product families yet.</p>
						{/each}
					</div>
				</FormField>

				<FormField
					label="Products it applies to"
					description="Individual products, on top of whatever the product families cover."
				>
					<div class="space-y-2">
						{#each products as product (product.uuid)}
							<label class="flex items-center gap-2 text-sm">
								<Checkbox
									checked={edit.appliesToProductUUIDs.includes(product.uuid)}
									disabled={saving}
									spacing=""
									class="shrink-0"
									on:change={event =>
										(edit.appliesToProductUUIDs = fnToggle(edit.appliesToProductUUIDs, product.uuid, event))}
								/>
								{product.name}
							</label>
						{:else}
							<p class="text-xs text-default">No products yet.</p>
						{/each}
					</div>
				</FormField>
			</div>
			<p class="text-xs text-default">
				{fnScopeSummary(edit.appliesToProductUUIDs, edit.appliesToFamilySlugs)} Restricting a coupon
				does not withdraw the discount from anyone who has already redeemed it.
			</p>
		</FormModal>
	{/if}

	<FormModal
		bind:open={isAdding}
		title="New coupon"
		faIcon="ticket"
		submitLabel="Create coupon"
		size="lg"
		busy={saving}
		description="Discount terms cannot be edited afterwards, because a coupon whose terms moved would price differently for people who already hold it."
		on:submit={fnCreate}
	>
			<div class="grid md:grid-cols-3 gap-4">
				<FormField id="coupon-code" label="Code" required description="What the customer types. Matched case-insensitively." error={codeError}>
					<Input id="coupon-code" bind:value={draft.code} placeholder="SAVE20" disabled={saving} />
				</FormField>
				<FormField id="coupon-type" label="Discount" required>
					<SelectField
						id="coupon-type"
						bind:value={draft.discountType}
						items={[
							{ value: 'percent', name: 'Percentage off', description: 'Takes a share of the price off, in every currency.' },
							{ value: 'fixed', name: 'Fixed amount off', description: 'Takes a set amount off, entered per currency.' }
						]}
						disabled={saving}
					/>
				</FormField>
				{#if draft.discountType === 'percent'}
					<FormField id="coupon-percent" label="Percent off" required>
						<Input id="coupon-percent" type="number" min="1" max="100" step="1" bind:value={draft.percentOff} disabled={saving} />
					</FormField>
				{/if}
				<FormField id="coupon-duration" label="Duration" required>
					<SelectField
						id="coupon-duration"
						bind:value={draft.duration}
						items={[
							{ value: 'once', name: 'First payment only', description: 'Applies to the first invoice; renewals are charged in full.' },
							{ value: 'repeating', name: 'A number of months', description: 'Applies to every invoice for the months you set, then stops.' },
							{ value: 'forever', name: 'For the life of the subscription', description: 'Applies to every invoice for as long as the subscription lasts.' }
						]}
						disabled={saving}
					/>
				</FormField>
				{#if draft.duration === 'repeating'}
					<FormField id="coupon-months" label="Months" required>
						<Input id="coupon-months" type="number" min="1" step="1" bind:value={draft.durationInMonths} disabled={saving} />
					</FormField>
				{/if}
				<FormField id="coupon-max" label="Redemption limit" description="Blank for unlimited.">
					<Input id="coupon-max" type="number" min="1" step="1" bind:value={draft.maxRedemptions} disabled={saving} />
				</FormField>
				<FormField id="coupon-expires" label="Expires" description="Blank for never.">
					<Input id="coupon-expires" type="date" bind:value={draft.expiresAt} disabled={saving} />
				</FormField>
			</div>

			<!--
				Absent from this form until now, which made the restriction reachable only by
				creating the coupon and then editing it — and an unrestricted coupon is live from
				the moment it is created, so the window between the two was a discount on
				everything in the catalogue.
			-->
			<div class="grid md:grid-cols-2 gap-4">
				<FormField
					label="Product families it applies to"
					description="A product family covers every product in it, including products added later."
				>
					<div class="space-y-2">
						{#each families as family (family.slug)}
							<label class="flex items-center gap-2 text-sm">
								<Checkbox
									checked={draft.appliesToFamilySlugs.includes(family.slug)}
									disabled={saving}
									spacing=""
									class="shrink-0"
									on:change={event =>
										(draft.appliesToFamilySlugs = fnToggle(draft.appliesToFamilySlugs, family.slug, event))}
								/>
								{family.name}
							</label>
						{:else}
							<p class="text-xs text-default">No product families yet.</p>
						{/each}
					</div>
				</FormField>

				<FormField
					label="Products it applies to"
					description="Individual products, on top of whatever the product families cover."
				>
					<div class="space-y-2">
						{#each products as product (product.uuid)}
							<label class="flex items-center gap-2 text-sm">
								<Checkbox
									checked={draft.appliesToProductUUIDs.includes(product.uuid)}
									disabled={saving}
									spacing=""
									class="shrink-0"
									on:change={event =>
										(draft.appliesToProductUUIDs = fnToggle(draft.appliesToProductUUIDs, product.uuid, event))}
								/>
								{product.name}
							</label>
						{:else}
							<p class="text-xs text-default">No products yet.</p>
						{/each}
					</div>
				</FormField>
			</div>
			<p class="text-xs text-default">
				{fnScopeSummary(draft.appliesToProductUUIDs, draft.appliesToFamilySlugs)}
			</p>

			{#if draft.discountType === 'fixed'}
				<div>
					<div class="card-label mb-2">
						<i class="fa-solid fa-coins text-[11px] mr-1" />Amounts off
					</div>
					<p class="text-xs text-default mb-2">
						Per currency, for the same reason prices are: a single amount plus currency makes the
						coupon unusable by every customer not in that currency.
					</p>
					<div class="grid md:grid-cols-3 gap-4">
						{#each currencies as currency (currency.code)}
							<FormField
								id={`coupon-amount-${currency.code}`}
								label={`${currency.code.toUpperCase()} (${currency.name})`}
								description={`Taken off: ${fnFormat(currency.code, fnMinorFrom(currency.code, `${draft.amounts[currency.code] ?? ''}`) ?? 0)}`}
							>
								<!-- Text, not `type="number"`: a number input rejects the comma half of
								     Europe types, and its spinner steps by whole units — on a decimal
								     field that means stepping a euro at a time. -->
								<Input
									id={`coupon-amount-${currency.code}`}
									type="text"
									inputmode="decimal"
									placeholder={majorUnitsFrom(0, currency.minorUnitDigits)}
									bind:value={draft.amounts[currency.code]}
									disabled={saving}
								/>
							</FormField>
						{/each}
					</div>
				</div>
			{/if}
	</FormModal>
</Panel>
